import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from './ui/button';
import { Warning, Info, Lightbulb, ArrowRight } from '@phosphor-icons/react';

const money = (v) => (v === null || v === undefined ? 'Kayıt yok' : formatCurrency(v));
const amount = (c, k = 'b') => (c[`${k}_count`] ? formatCurrency(c[`${k}_total`]) : 'Kayıt yok');
const pctText = (v) => (v === null || v === undefined ? null : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);

const SummaryCard = ({ label, value, sub, pct, testid }) => (
  <div className="border border-border bg-card rounded-xl p-4" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold font-data mt-1">{value}</p>
    <div className="flex items-center justify-between text-xs mt-1 gap-2">
      <span className="text-muted-foreground truncate">{sub}</span>
      {pct && <span className="font-medium shrink-0">{pct}</span>}
    </div>
  </div>
);

const PURCHASE_KEYS = ['kirmizi_et', 'tavuk', 'balik', 'sakatat', 'sebze_manav', 'genel_alim'];

// Yalnızca admin ve açık yetkili kullanıcılara görünür; hata olursa ana sayfanın geri kalanını etkilemez
export const SmartSummary = () => {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('smart_analytics', 'view');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await api.get('/smart-analytics/summary', { timeout: 30000 });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Özet yüklenemedi');
    }
  };

  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return null;

  return (
    <section className="space-y-3" data-testid="smart-summary">
      <div className="flex flex-wrap items-center justify-between gap-2 pl-12 lg:pl-0">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Lightbulb size={20} /> Akıllı Yönetici Özeti</h2>
        <Link to="/smart-analytics" className="text-sm text-primary flex items-center gap-1" data-testid="smart-summary-detail-link">Ayrıntılı analiz <ArrowRight size={14} /></Link>
      </div>
      {error && (
        <div className="p-3 border border-destructive/50 rounded-lg text-sm text-destructive flex items-center gap-3" data-testid="smart-summary-error">
          <span>Özet yüklenemedi: {error}</span>
          <Button size="sm" variant="outline" onClick={load} data-testid="smart-summary-retry">Yeniden Dene</Button>
        </div>
      )}
      {!data && !error && <p className="text-sm text-muted-foreground" data-testid="smart-summary-loading">Özet hesaplanıyor...</p>}
      {data && <SummaryBody d={data} />}
    </section>
  );
};

const SummaryBody = ({ d }) => {
  const rev = d.revenue;
  const sub = `${d.period_b.start.slice(8)}–${d.period_b.end.slice(8)} · geçen ayın aynı günlerine göre`;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <SummaryCard testid="smart-summary-yesterday" label={`Dünkü ciro (${d.yesterday.date})`} value={money(d.yesterday.revenue)} sub={d.today.revenue != null ? `Bugün: ${formatCurrency(d.today.revenue)}` : 'Bugün: kayıt yok'} />
        <SummaryCard testid="smart-summary-pos" label="Orion POS net satış (bu ay)" value={d.pos.month_net_sales == null ? 'POS raporu yüklenmedi' : formatCurrency(d.pos.month_net_sales)} sub={`POS kapsama %${d.pos.month_coverage_pct} · dün: ${d.pos.yesterday_net_sales == null ? 'rapor yok' : formatCurrency(d.pos.yesterday_net_sales)}`} pct={d.pos.warnings ? `${d.pos.warnings} uyarı` : null} />
        <SummaryCard testid="smart-summary-month-revenue" label="Bu ay ciro (ERP)" value={money(rev.b_total)} sub={`Kapsama %${rev.b_coverage_pct}`} pct={rev.reliable ? pctText(rev.pct) : 'Güvenilmez'} />
        <SummaryCard testid="smart-summary-purchases" label="Bu ay satın alma" value={amount(d.purchases_total)} sub={d.purchases_total.b_ratio_to_revenue != null ? `Satın alma/ciro %${d.purchases_total.b_ratio_to_revenue}` : sub} pct={pctText(d.purchases_total.pct)} />
        <SummaryCard testid="smart-summary-expenses" label="Bu ay muhasebe giderleri" value={amount(d.expenses_total)} sub={d.expenses_total.b_ratio_to_revenue != null ? `Gider/ciro %${d.expenses_total.b_ratio_to_revenue}` : sub} pct={pctText(d.expenses_total.pct)} />
        <SummaryCard testid="smart-summary-checks" label="Kontrol / uyarı" value={`${d.counts.control_required} kontrol · ${d.counts.alerts_total} uyarı`} sub={`${d.counts.missing_revenue_days} gün ciro eksik · ${d.counts.excluded_expenses} hariç gider`} />
        {PURCHASE_KEYS.map((k) => (
          <SummaryCard key={k} testid={`smart-summary-${k}`} label={d.purchases[k].label} value={amount(d.purchases[k])} sub={d.purchases[k].b_ratio_to_revenue != null ? `Satın alma/ciro %${d.purchases[k].b_ratio_to_revenue}` : `Önceki: ${amount(d.purchases[k], 'a')}`} pct={pctText(d.purchases[k].pct)} />
        ))}
      </div>
      {d.alerts.length > 0 && (
        <div className="border border-border bg-card rounded-xl p-3 space-y-1 text-sm" data-testid="smart-summary-alerts">
          {d.alerts.map((a, i) => (
            <div key={i} className="flex gap-2 items-start">
              {a.level === 'warning' ? <Warning size={16} className="text-amber-600 mt-0.5 shrink-0" /> : <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />}
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
